/**
 * Step 10 Mobile: Review
 * Read-only accordion summary of all wizard steps with edit links.
 */

import type { AllStepFormData, PropertyItem, SupplementaryIncomeItem, SpendingPhase } from '../../types/wizard';
import { WizardMobileShell } from './WizardMobileShell';

interface StepReviewProps {
  formData: Partial<AllStepFormData>;
  onEditStep: (step: number) => void;
  onViewProjection: () => void;
  onBack: () => void;
  isSubmitting: boolean;
  isAlternative?: boolean;
  projectionReady?: boolean;
  autoProjectionError?: string | null;
}

const sections: { step: number; title: string; summary: (d: Partial<AllStepFormData>) => string }[] = [
  { step: 1, title: 'Basics', summary: d => `${d.planName || 'Unnamed'} · ${d.planType === 'JOINT' ? 'Joint' : 'Single'}` },
  { step: 2, title: 'People', summary: d => `${d.persons?.[0]?.fullName || 'You'}${d.planType === 'JOINT' && d.persons?.[1]?.fullName ? ` & ${d.persons[1].fullName}` : ''}` },
  { step: 3, title: 'Investments', summary: d => {
    const h = [...(d.holdings?.person0 || []), ...(d.holdings?.person1 || [])];
    return h.length ? `${h.length} holdings` : 'No holdings';
  }},
  { step: 4, title: 'Property', summary: d => {
    const props = d.properties || [];
    if (!props.length) return 'No property';
    const eq = props.reduce((s: number, p: PropertyItem) => s + ((p.currentMarketValue || 0) - (p.currentMortgageAmount || 0)), 0);
    return `${props.length} properties · £${(eq / 1000).toFixed(0)}K equity`;
  }},
  { step: 5, title: 'Income', summary: d => {
    const inc = d.supplementaryIncome || [];
    if (!inc.length) return 'No extra income';
    const tot = inc.reduce((s: number, i: SupplementaryIncomeItem) => s + (i.grossAnnualAmount || 0), 0);
    return `${inc.length} sources · £${(tot / 1000).toFixed(0)}K/yr`;
  }},
  { step: 6, title: 'Spending', summary: d => {
    const phases = d.spendingPhases ?? [];
    const peak = Math.max(...phases.map((p: SpendingPhase) => (p.livingExpenses || 0) + (p.travelLeisure || 0) + (p.otherDiscretionary || 0)), 0);
    return `${phases.length} phases · £${(peak / 1000).toFixed(0)}K/yr peak`;
  }},
  { step: 7, title: 'Later Life', summary: d => `${d.careScenarios?.length || 0} care scenarios` },
  { step: 8, title: 'Assumptions', summary: d => {
    const e = d.economicAssumptions;
    return e ? `${e.equityReturnAnnualPct}% equity · ${e.bondReturnAnnualPct}% bond` : 'Defaults';
  }},
  { step: 9, title: 'Strategy', summary: d => `${d.decumulationStrategy || 'TAX_OPTIMISED'} · ${d.projectionYears || 30}yr` },
];

export default function StepReviewMobile({ formData, onEditStep, onViewProjection, onBack, isSubmitting, projectionReady, autoProjectionError }: Readonly<StepReviewProps>) {
  return (
    <WizardMobileShell step={10} totalSteps={10} title="Review" onBack={onBack} onContinue={onViewProjection} lastSaved={projectionReady ? 'Projection ready' : undefined}>
      <div className="mobile-step-form">
        {projectionReady && (
          <button type="button" className="mobile-btn mobile-btn--primary mobile-btn--full" onClick={onViewProjection} disabled={isSubmitting}>
            {isSubmitting ? 'Generating...' : '✨ See your projection'}
          </button>
        )}
        {autoProjectionError && <div className="auth-mobile__error">{autoProjectionError}</div>}

        {sections.map(s => (
          <div key={s.step} className="mobile-review-section">
            <button type="button" className="mobile-review-section__header" onClick={() => onEditStep(s.step)}>
              <span className="mobile-review-section__title">{s.title}</span>
              <span className="mobile-review-section__summary">{s.summary(formData)}</span>
              <span className="mobile-review-section__edit">✎</span>
            </button>
          </div>
        ))}

        <p className="mobile-disclaimer">
          This projection is for educational purposes only and does not constitute financial advice.
          Past performance is not a guarantee of future results.
        </p>
      </div>
    </WizardMobileShell>
  );
}
